import React, { useEffect, useState } from "react";
import { api, fmtDate, RESERVATION_TYPE_LABELS, STATUS_BADGE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Briefcase, Check, X, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Commercial() {
  const { user } = useAuth();
  const t = useT();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const [r, u] = await Promise.all([
      api.get("/reservations"),
      api.get("/users"),
    ]);
    setRows(r.data.filter(x => x.reservation_type === "charter_skippered" || x.reservation_type === "excursion"));
    setUsers(u.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const filtered = tab === "pending" ? rows.filter(r => r.status === "pending_approval")
    : tab === "upcoming" ? rows.filter(r => r.status === "approved" && r.end_date >= new Date().toISOString().slice(0,10))
    : tab === "completed" ? rows.filter(r => r.status === "approved" && r.end_date < new Date().toISOString().slice(0,10))
    : rows.filter(r => r.status === "rejected" || r.status === "cancelled");

  const isDirector = user?.role === "owner_director" || user?.role === "super_admin";

  const vote = async (id, decision, note) => {
    try {
      await api.post(`/reservations/${id}/approve`, { decision, note });
      toast.success(`Vote recorded: ${decision}`); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Vote failed"); }
  };

  const totalRevenue = rows.filter(r => r.status === "approved").reduce((s, r) => s + (r.trip_price || 0), 0);

  return (
    <div className="space-y-6" data-testid="commercial-page">
      <div>
        <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("commercial.subtitle")}</div>
        <h2 className="font-display text-2xl font-bold tracking-tight">{t("commercial.title")}</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("commercial.stat.pending")} value={rows.filter(r => r.status === "pending_approval").length} tone="amber" />
        <Stat label={t("commercial.stat.approved")} value={rows.filter(r => r.status === "approved").length} tone="emerald" />
        <Stat label={t("commercial.stat.rejected")} value={rows.filter(r => r.status === "rejected").length} tone="red" />
        <Stat label={t("commercial.stat.revenue")} value={`€ ${totalRevenue.toFixed(0)}`} icon={Banknote} tone="blue" />
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { k: "pending", labelKey: "commercial.tab.pending" },
          { k: "upcoming", labelKey: "commercial.tab.upcoming" },
          { k: "completed", labelKey: "commercial.tab.completed" },
          { k: "archive", labelKey: "commercial.tab.archive" },
        ].map(({k, labelKey}) => (
          <button key={k} onClick={() => setTab(k)}
            data-testid={`commercial-tab-${k}`}
            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${tab === k ? "bg-[#007AFF]/15 border-[#007AFF]/50 text-[#F8FAFC]" : "bg-[#0B1221] border-[#1E293B] text-[#94A3B8] hover:border-[#334155]"}`}>
            {t(labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-64 ledger-card animate-pulse" />
      ) : filtered.length === 0 ? (
        <div className="ledger-card p-12 text-center text-[#94A3B8]">
          <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">{t("commercial.empty")}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <CommercialRow key={r.id} row={r} users={users} isDirector={isDirector} userId={user?.id} onVote={vote} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function CommercialRow({ row, users, isDirector, userId, onVote, t }) {
  const [note, setNote] = useState("");
  const [expand, setExpand] = useState(false);
  const canVote = isDirector && row.status === "pending_approval" &&
    !(row.approvals || []).some(a => a.director_user_id === userId);

  return (
    <div className="ledger-card p-4" data-testid={`commercial-row-${row.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`badge-pill ${STATUS_BADGE[row.status] || "badge-neutral"}`}>{t(`status.${row.status}`)}</span>
            <span className="badge-pill badge-neutral">{t(`reservation.${row.reservation_type}`)}</span>
            <span className="font-mono text-[10px] text-[#94A3B8]">{t("commercial.votesOf", { done: (row.approvals || []).length })}</span>
          </div>
          <div className="font-medium">{row.client_name || t("commercial.unnamedClient")} · {fmtDate(row.start_date)} → {fmtDate(row.end_date)}</div>
          <div className="font-mono text-[11px] text-[#94A3B8]">
            {row.passenger_count ? `${t("commercial.pax", { count: row.passenger_count })} · ` : ""}{row.trip_price ? `€ ${row.trip_price}` : t("commercial.noPrice")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canVote && (
            <>
              <Button size="sm" onClick={() => onVote(row.id, "approved", note)} data-testid={`approve-${row.id}`} className="bg-[#10B981] hover:bg-[#059669] h-8"><Check className="w-3 h-3 mr-1" /> {t("commercial.approve")}</Button>
              <Button size="sm" onClick={() => onVote(row.id, "rejected", note)} data-testid={`reject-${row.id}`} className="bg-[#EF4444] hover:bg-[#DC2626] h-8"><X className="w-3 h-3 mr-1" /> {t("commercial.reject")}</Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => setExpand(!expand)} className="bg-transparent border-[#1E293B] h-8">{expand ? t("common.hide") : t("common.details")}</Button>
        </div>
      </div>
      {expand && (
        <div className="mt-4 pt-4 border-t border-[#1E293B] space-y-3">
          {(row.approvals || []).length > 0 && (
            <div>
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mb-1">{t("commercial.voteHistory")}</div>
              {row.approvals.map((a, i) => (
                <div key={i} className="text-xs flex items-center gap-2"><span className={`badge-pill ${a.decision === "approved" ? "badge-approved" : "badge-rejected"}`}>{t(`status.${a.decision}`)}</span> {a.director_name} {a.decision_note ? `· "${a.decision_note}"` : ""}</div>
              ))}
            </div>
          )}
          {row.client_contact && <div className="text-xs"><span className="text-[#94A3B8] font-mono text-[10px]">{t("commercial.contact")} · </span>{row.client_contact}</div>}
          {row.notes && <div className="text-xs"><span className="text-[#94A3B8] font-mono text-[10px]">{t("commercial.notes")} · </span>{row.notes}</div>}
          {canVote && (
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("commercial.votePlaceholder")} data-testid={`vote-note-${row.id}`} className="bg-[#050A14] border-[#1E293B]" />
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  const toneMap = {
    amber: "text-[#FBBF24] border-[#F59E0B]/30 bg-[#F59E0B]/10",
    emerald: "text-[#34D399] border-[#10B981]/30 bg-[#10B981]/10",
    red: "text-[#F87171] border-[#EF4444]/30 bg-[#EF4444]/10",
    blue: "text-[#4DA6FF] border-[#007AFF]/30 bg-[#007AFF]/10",
  };
  return (
    <div className="ledger-card p-4">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-md border ${toneMap[tone]}`}>
        {Icon ? <Icon className="w-4 h-4" /> : <span className="font-mono text-xs">●</span>}
      </div>
      <div className="font-mono text-[10px] tracking-widest text-[#94A3B8] mt-2">{label}</div>
      <div className="font-display text-xl font-bold tracking-tight">{value}</div>
    </div>
  );
}
